import { useEffect } from 'react'

import { IDB } from '@/core/db'
import { useDbClientContext } from '@/context/__project__/DbClientContext'

type UseTableSubscriptionParams = {
  dbClient: IDB | undefined // database client
  tableIds: string[] // table ids to subscribe to
  callback: () => void // callback function to execute when table changes
}

/**
 * Custom hook to handle table subscriptions of DbTable instance from DbClient
 */
export function useTableSubscription({ dbClient, tableIds, callback }: UseTableSubscriptionParams) {
  const { dbWorker, loadedTables, language } = useDbClientContext()

  useEffect(() => {
    if (!dbClient || !dbWorker) return

    // Initial callback execution
    callback()

    let unsubs = []

    if (tableIds.length > 0) {
      for (const tableId of tableIds) {
        const dbTable = dbClient.tablesMap.get(tableId)
        if (!dbTable) continue

        if (!loadedTables.has(dbTable.id)) {
          dbWorker.sendAction({
            type: 'get_table_data',
            payload: {
              table_id: dbTable.id,
              language: language,
            },
            meta: {
              from: 'useTableSubscription',
            },
          })
        }

        const unsubscribe = dbTable.subscribe(callback)
        unsubs.push(unsubscribe)
      }
    }

    return () => {
      if (unsubs.length > 0) {
        unsubs.forEach((unsub) => unsub())
      }
    }
  }, [dbClient, tableIds, callback, loadedTables, language, dbWorker])
}
